import { useEffect, useState } from 'react'
import { db } from '../datos/db.js'
import { encolar } from '../datos/sync.js'
import { uuidv7 } from '../datos/uuid.js'
import { guaranies } from '../datos/formato.js'
import { Boton } from './Boton.js'

/**
 * Los dos formularios del dinero, compartidos por Cobros y por Atención.
 *
 * Viven acá y no en cada pantalla porque el mismo cobro se hace desde el
 * mostrador y desde el sillón: dos copias divergen —una gana un campo, la otra
 * un `default` distinto— y el día que no cuadre la caja nadie va a saber cuál
 * de las dos lo escribió.
 *
 * Cada uno escribe en Dexie **antes** de encolar: sin eso, sin conexión, quien
 * cobra aprieta el botón y no ve nada hasta que vuelva la red.
 */

const MEDIOS = [
  { id: 'efectivo', nombre: 'Efectivo' },
  { id: 'tarjeta', nombre: 'Tarjeta' },
  { id: 'transferencia', nombre: 'Transferencia' },
  { id: 'cheque', nombre: 'Cheque' },
  { id: 'seguro', nombre: 'Seguro' },
] as const

export interface ServicioMin {
  id: string
  nombre: string
  precio: number
  activo: boolean
}

export function FormularioCargo({
  citaId,
  recursosCita,
  servicios,
  onListo,
  className = 'border-t border-slate-100 bg-slate-50/60 px-4 py-3',
  servicioSugeridoId,
  piezaEstadoSugeridoId,
}: {
  citaId: string
  recursosCita: string[]
  servicios: ServicioMin[]
  onListo: () => void
  /** Cobros lo muestra pegado a la fila; Atención, como tarjeta suelta. */
  className?: string
  servicioSugeridoId?: string | null
  piezaEstadoSugeridoId?: string | null
}) {
  const [servicioId, setServicioId] = useState('')
  const [override, setOverride] = useState<number | null>(null)
  const [guardando, setGuardando] = useState(false)

  const activos = servicios.filter((s) => s.activo)
  const elegido = activos.find((s) => s.id === servicioId)
  useEffect(() => {
    if (servicioSugeridoId) setServicioId(servicioSugeridoId)
  }, [servicioSugeridoId])

  // El monto **se deriva del servicio**, no se tipea. Tipearlo en cada cobro es
  // pedirle a la recepción que recuerde la lista de precios, y es donde se
  // cuelan los ceros de más. El override existe para el descuento puntual y
  // está escondido porque es la excepción, no lo habitual.
  const monto = override ?? elegido?.precio ?? 0

  return (
    <form
      className={className}
      onSubmit={async (ev) => {
        ev.preventDefault()
        if (!servicioId) return
        setGuardando(true)
        const cuerpo = {
          citaId,
          servicioId,
          piezaEstadoId: piezaEstadoSugeridoId ?? null,
          // Quién lo produjo: es la columna sobre la que se arma el reporte de
          // producción por profesional.
          recursoPersonaId: recursosCita[0] ?? null,
          monto,
        }
        // Se escribe en Dexie ANTES de encolar: sin esto, sin conexión la
        // recepcionista aprieta "Registrar" y no ve nada hasta que vuelva la
        // red. Si la mutación se rechaza, el deshacer-optimismo lo borra.
        const cargoId = uuidv7()
        await db.cargo.add({
          id: cargoId,
          cita_id: citaId,
          servicio_id: servicioId,
          pieza_estado_id: piezaEstadoSugeridoId ?? null,
          recurso_persona_id: cuerpo.recursoPersonaId,
          monto,
          moneda: 'PYG',
          updated_at: new Date().toISOString(),
          deleted_at: null,
        })
        await encolar('crear_cargo', { cargoId, ...cuerpo })
        setGuardando(false)
        setServicioId('')
        setOverride(null)
        onListo()
      }}
    >
      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-0 flex-1">
          <span className="txt-meta block">Servicio</span>
          <select
            value={servicioId}
            onChange={(e) => {
              setServicioId(e.target.value)
              setOverride(null)
            }}
            required
            autoFocus
            className="mt-1 h-9 w-full rounded-lg border border-slate-300 bg-white px-2 text-[14px]"
          >
            <option value="">Elegí…</option>
            {activos.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nombre} — {guaranies(s.precio)}
              </option>
            ))}
          </select>
        </label>

        <div className="w-32">
          <span className="txt-meta block">Monto</span>
          {override === null ? (
            <p className="mt-1 flex h-9 items-center justify-end text-[14px] font-medium tabular-nums text-slate-900">
              {guaranies(monto)}
            </p>
          ) : (
            <input
              type="number"
              min={0}
              step={1}
              value={override}
              onChange={(e) => setOverride(Number(e.target.value))}
              autoFocus
              className="mt-1 h-9 w-full rounded-lg border border-slate-300 px-2 text-right text-[14px] tabular-nums"
            />
          )}
        </div>

        <Boton variante="primary" type="submit" disabled={guardando || !servicioId}>
          Registrar
        </Boton>
      </div>

      {elegido && (
        <button
          type="button"
          onClick={() => setOverride(override === null ? elegido.precio : null)}
          className="txt-meta mt-2 underline decoration-dotted hover:text-slate-600"
        >
          {override === null ? 'Cobrar otro monto' : 'Usar el precio de lista'}
        </button>
      )}
    </form>
  )
}

export function FormularioPago({
  cargoId,
  saldo,
  sugerirTercero,
  destinoInicial,
}: {
  cargoId: string
  saldo: number
  sugerirTercero: boolean
  /** Dónde entra la plata. El default sale del rol de quien cobra. */
  destinoInicial?: 'caja' | 'profesional'
}) {
  const [monto, setMonto] = useState(saldo)
  const [medio, setMedio] = useState<string>('efectivo')
  const [pagadorTipo, setPagadorTipo] = useState<'paciente' | 'tercero'>(
    sugerirTercero ? 'tercero' : 'paciente',
  )
  const [destino, setDestino] = useState<'caja' | 'profesional'>(destinoInicial ?? 'caja')
  const [guardando, setGuardando] = useState(false)

  const excede = monto > saldo

  return (
    <form
      className="flex flex-wrap items-end gap-3"
      onSubmit={async (ev) => {
        ev.preventDefault()
        if (excede || monto <= 0) return
        setGuardando(true)
        const pagoId = uuidv7()
        const destinoPago = pagadorTipo === 'tercero' ? 'aseguradora' : destino
        await db.pago.add({
          id: pagoId,
          cargo_id: cargoId,
          monto,
          medio,
          pagador_tipo: pagadorTipo,
          destino: destinoPago,
          fecha: new Date().toISOString(),
          referencia: null,
          updated_at: new Date().toISOString(),
          deleted_at: null,
        })
        await encolar('crear_pago', {
          pagoId, cargoId, monto, medio, pagadorTipo, destino: destinoPago,
        })
        setGuardando(false)
      }}
    >
      <label className="w-32">
        <span className="txt-meta block">Cobrar</span>
        <input
          type="number"
          min={1}
          max={saldo}
          // `step` se ancla al `min`: con step de mil y min de uno, el navegador
          // sólo aceptaría 1, 1001, 2001… y rechazaría 50.000 sin decir nada
          // entendible. Un guaraní es entero, no múltiplo de mil.
          step={1}
          value={monto}
          onChange={(e) => setMonto(Number(e.target.value))}
          className={`mt-1 h-9 w-full rounded-lg border px-2 text-right text-[14px] tabular-nums ${
            excede ? 'border-rose-400' : 'border-slate-300'
          }`}
        />
      </label>

      <label>
        <span className="txt-meta block">Medio</span>
        <select
          value={medio}
          onChange={(e) => setMedio(e.target.value)}
          className="mt-1 h-9 rounded-lg border border-slate-300 bg-white px-2 text-[14px]"
        >
          {MEDIOS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.nombre}
            </option>
          ))}
        </select>
      </label>

      <label>
        <span className="txt-meta block">Entra en</span>
        <select
          value={destino}
          onChange={(e) => setDestino(e.target.value as 'caja' | 'profesional')}
          disabled={pagadorTipo === 'tercero'}
          className="mt-1 h-9 rounded-lg border border-slate-300 bg-white px-2 text-[14px]"
        >
          <option value="caja">Caja del mostrador</option>
          <option value="profesional">Se lo llevó el profesional</option>
        </select>
        {pagadorTipo === 'tercero' && (
          <span className="txt-meta mt-1 block">La plata del seguro no entra al cajón.</span>
        )}
      </label>

      <label>
        <span className="txt-meta block">Paga</span>
        <select
          value={pagadorTipo}
          onChange={(e) => setPagadorTipo(e.target.value as 'paciente' | 'tercero')}
          className="mt-1 h-9 rounded-lg border border-slate-300 bg-white px-2 text-[14px]"
        >
          <option value="paciente">El paciente</option>
          <option value="tercero">El seguro</option>
        </select>
      </label>

      <Boton variante="primary" type="submit" disabled={guardando || excede || monto <= 0}>
        Cobrar
      </Boton>

      {excede && (
        <span className="text-[12px] text-rose-700">
          Es más que el saldo. Cobrar de más es plata que después hay que devolver.
        </span>
      )}
    </form>
  )
}
